import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Search } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { USGSetup as USGSetupType, TestName } from '../../types/diagnosis';
import toast from 'react-hot-toast';

export default function USGSetup() {
  const [setups, setSetups] = useState<USGSetupType[]>([]);
  const [tests, setTests] = useState<TestName[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingSetup, setEditingSetup] = useState<USGSetupType | null>(null);
  const [formData, setFormData] = useState({
    test_id: '',
    test_code: '',
    body_part: '',
    preparation_instructions: '',
    duration_minutes: '',
  });

  useEffect(() => {
    loadTests();
    loadSetups();
  }, []);

  const loadTests = async () => {
    try {
      const { data, error } = await supabase
        .from('test_names')
        .select('*')
        .order('test_name', { ascending: true });

      if (error) throw error;
      setTests(data || []);
    } catch (error: any) {
      console.error('Error loading tests:', error);
      toast.error('Failed to load tests');
    }
  };

  const loadSetups = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('usg_setup')
        .select(`
          *,
          test:test_names(*)
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setSetups(data || []);
    } catch (error: any) {
      console.error('Error loading setups:', error);
      toast.error('Failed to load USG setups');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const dataToSave = {
        ...formData,
        duration_minutes: formData.duration_minutes ? parseInt(formData.duration_minutes) : null,
      };

      if (editingSetup) {
        const { error } = await supabase
          .from('usg_setup')
          .update(dataToSave)
          .eq('id', editingSetup.id);

        if (error) throw error;
        toast.success('Setup updated successfully');
      } else {
        const { error } = await supabase
          .from('usg_setup')
          .insert([dataToSave]);

        if (error) throw error;
        toast.success('Setup added successfully');
      }

      setShowForm(false);
      setEditingSetup(null);
      setFormData({
        test_id: '',
        test_code: '',
        body_part: '',
        preparation_instructions: '',
        duration_minutes: '',
      });
      loadSetups();
    } catch (error: any) {
      console.error('Error saving setup:', error);
      toast.error(error.message || 'Failed to save setup');
    }
  };

  const handleEdit = (setup: USGSetupType) => {
    setEditingSetup(setup);
    setFormData({
      test_id: setup.test_id,
      test_code: setup.test_code || '',
      body_part: setup.body_part || '',
      preparation_instructions: setup.preparation_instructions || '',
      duration_minutes: setup.duration_minutes?.toString() || '',
    });
    setShowForm(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this setup?')) return;

    try {
      const { error } = await supabase
        .from('usg_setup')
        .delete()
        .eq('id', id);

      if (error) throw error;
      toast.success('Setup deleted successfully');
      loadSetups();
    } catch (error: any) {
      console.error('Error deleting setup:', error);
      toast.error('Failed to delete setup');
    }
  };

  const filteredSetups = setups.filter((setup) =>
    setup.test?.test_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    setup.body_part?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">USG Setup</h1>
        <p className="text-gray-600">Configure ultrasound test parameters</p>
      </div>

      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex justify-between items-center mb-6">
          <div className="flex-1 max-w-md">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search setups..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
          </div>
          <button
            onClick={() => {
              setShowForm(true);
              setEditingSetup(null);
              setFormData({
                test_id: '',
                test_code: '',
                body_part: '',
                preparation_instructions: '',
                duration_minutes: '',
              });
            }}
            className="ml-4 flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
          >
            <Plus className="w-5 h-5 mr-2" />
            Add Setup
          </button>
        </div>

        {showForm && (
          <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <h3 className="text-lg font-semibold mb-4">
              {editingSetup ? 'Edit Setup' : 'Add New Setup'}
            </h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Test <span className="text-red-500">*</span>
                  </label>
                  <select
                    required
                    value={formData.test_id}
                    onChange={(e) => setFormData({ ...formData, test_id: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    <option value="">--Select Test--</option>
                    {tests.map((test) => (
                      <option key={test.id} value={test.id}>
                        {test.test_name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Test Code
                  </label>
                  <input
                    type="text"
                    value={formData.test_code}
                    onChange={(e) => setFormData({ ...formData, test_code: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Body Part
                  </label>
                  <input
                    type="text"
                    value={formData.body_part}
                    onChange={(e) => setFormData({ ...formData, body_part: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="e.g., Abdomen, Pelvis, Breast"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Duration (minutes)
                  </label>
                  <input
                    type="number"
                    value={formData.duration_minutes}
                    onChange={(e) => setFormData({ ...formData, duration_minutes: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="e.g., 30"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Preparation Instructions
                </label>
                <textarea
                  value={formData.preparation_instructions}
                  onChange={(e) => setFormData({ ...formData, preparation_instructions: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="e.g., Fasting required, Full bladder needed"
                />
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowForm(false);
                    setEditingSetup(null);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                >
                  {editingSetup ? 'Update' : 'Add'} Setup
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  SL
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Test Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Test Code
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Body Part
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Duration
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Action
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={6} className="px-6 py-4 text-center text-gray-500">
                    Loading...
                  </td>
                </tr>
              ) : filteredSetups.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-4 text-center text-gray-500">
                    No setups found
                  </td>
                </tr>
              ) : (
                filteredSetups.map((setup, index) => (
                  <tr key={setup.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {index + 1}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {setup.test?.test_name || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {setup.test_code || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {setup.body_part || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {setup.duration_minutes ? `${setup.duration_minutes} min` : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => handleEdit(setup)}
                        className="text-primary-600 hover:text-primary-900 mr-4"
                      >
                        <Edit2 className="w-4 h-4 inline" />
                      </button>
                      <button
                        onClick={() => handleDelete(setup.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        <Trash2 className="w-4 h-4 inline" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
